import { Box, Typography, Button, Container } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Home_IMG } from '../../Constants/ImageConstants';
import { COLORS } from '../../Constants/styles';

const headings = [
  { before: 'Structured Sports & ', highlight: 'Physical Education', after: ' for Schools' },
  { before: 'Where Every Child Discovers the ', highlight: 'Joy of Sport', after: '' },
  { before: 'Curriculum-led. Platform-supported. ', highlight: 'Institution-aligned.', after: '' },
];

const slideUp = (delay = 0) => ({
  initial: { opacity: 0, y: 60 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, ease: 'easeOut', delay },
});

export const HeroSection = () => {
  const [headingIndex, setHeadingIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setHeadingIndex((prev) => (prev + 1) % headings.length);
    }, 7000);
    return () => clearInterval(timer);
  }, []);

  return (
    <Box
      sx={{
        backgroundImage: `url(${Home_IMG.hero})`,
        backgroundSize: 'cover',
        backgroundPosition: 'top center',
        backgroundRepeat: 'no-repeat',
        width: '100%',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Full-image dark overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.38)',
          zIndex: 1,
        }}
      />

      {/* Hero content */}
      <Container
        maxWidth="md"
        sx={{
          position: 'relative',
          zIndex: 2,
          textAlign: 'center',
          py: { xs: 10, md: 14 },
          px: { xs: 3, md: 4 },
        }}
      >
        {/* Badge */}
        <motion.div {...slideUp(0)}>
          <Typography
            variant="subtitle1"
            sx={{
              backgroundColor: COLORS.accentLime,
              color: COLORS.pureBlack,
              px: 2.5,
              py: 0.6,
              borderRadius: '4px',
              display: 'inline-block',
              mb: { xs: 3, md: 4 },
            }}
          >
            Welcome to Grassroots Sports
          </Typography>
        </motion.div>

        {/* Heading — cycles every 7s with slide-up/out animation */}
        <Box sx={{ mb: { xs: 3, md: 4 }, minHeight: { xs: '5rem', sm: '7rem', md: '9rem' } }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={headingIndex}
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -40 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
            >
              <Box
                component="h1"
                style={{ fontFamily: 'Big Shoulders Text', fontStyle: 'italic', fontWeight: 900 }}
                sx={{
                  m: 0,
                  fontSize: { xs: '2.2rem', sm: '3rem', md: '4rem', lg: '4.5rem' },
                  lineHeight: 1,
                  color: COLORS.white,
                  textTransform: 'capitalize',
                }}
              >
                {headings[headingIndex].before}
                <Box component="span" sx={{ color: COLORS.accentLime }}>
                  {headings[headingIndex].highlight}
                </Box>
                {headings[headingIndex].after}
              </Box>
            </motion.div>
          </AnimatePresence>
        </Box>

        {/* Subtitle */}
        <motion.div {...slideUp(0.3)}>
          <Typography
            variant="body1"
            sx={{
              color: COLORS.whiteSemiTransparent,
              mb: { xs: 5, md: 6 },
              maxWidth: 560,
              mx: 'auto',
              textTransform: 'none',
              lineHeight: 1.7,
            }}
          >
            Helping schools nurture healthier, happier, and more confident children through engaging Sports & Physical Education from KG to Grade 12
          </Typography>
        </motion.div>

        {/* CTA buttons */}
        <motion.div {...slideUp(0.45)}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              href=""
              sx={{
                bgcolor: COLORS.accentLime,
                color: COLORS.accentDarkGreen,
                border: `1px solid ${COLORS.white}`,
                fontWeight: 600,
                fontFamily: "'Big Shoulders Text', sans-serif",
                textTransform: 'uppercase',
                fontSize: { xs: '0.9rem', md: '1rem' },
                letterSpacing: '1px',
                px: { xs: 3.5, md: 5 },
                py: 1.5,
                borderRadius: '50px',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: COLORS.accentDarkGreen,
                  color: COLORS.accentLime,
                  border: `1px solid ${COLORS.accentLime}`,
                  boxShadow: 'none',
                },
              }}
            >
              Explore Our Framework
            </Button>

            <Button
              variant="contained"
              href="/"
              sx={{
               bgcolor: COLORS.white,
                color: COLORS.accentDarkGreen,
                border: `1px solid ${COLORS.accentDarkGreen}`,
                fontWeight: 600,
                fontFamily: "'Big Shoulders Text', sans-serif",
                textTransform: 'uppercase',
                fontSize: { xs: '0.9rem', md: '1rem' },
                letterSpacing: '1px',
                px: { xs: 3.5, md: 5 },
                py: 1.5,
                borderRadius: '50px',
                boxShadow: 'none',
                 '&:hover': {
                  bgcolor: COLORS.accentDarkGreen,
                  color: COLORS.accentLime,
                  border: `1px solid ${COLORS.accentLime}`,
                  boxShadow: 'none',
                },
              }}
            >
              Partner With Us
            </Button>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};
